import { useAppState } from '../AppContext';
import React, { useState } from 'react';
import { Button, ButtonGroup, Modal } from 'react-bootstrap';
import Pdf from './Pdf';
import PropTypes from 'prop-types';
import './PdfPreviewModal.css';
import {
  copyToClipboard,
  getAvailableHeight,
  getAvailableWidth,
  getDisplayName
} from './utils';
import Highlighter from 'react-highlight-words';
import { FaArrowLeft, FaCopy } from 'react-icons/fa';

const Snippet = ({ document, snip, highlightSnippet }) => {
  let [{ selectedSnippet, searchString }] = useAppState();

  return (
    <li style={{ paddingTop: '0.75em' }}>
      <Highlighter
        textToHighlight={snip.text}
        searchWords={searchString.split(' ')}
        highlightStyle={{ backgroundColor: '#ffd54f' }}
        style={{
          backgroundColor:
            snip.id === selectedSnippet ? 'rgb(232, 242, 252)' : null
        }}
      />
      &nbsp;{' '}
      <Button
        onClick={() => copyToClipboard(snip.text)}
        variant="light"
        size="sm"
      >
        <FaCopy />
      </Button>
      <br />
      <Button
        onClick={() => highlightSnippet(snip, document)}
        variant="custom"
        size="sm"
      >
        <FaArrowLeft color={snip.id === selectedSnippet ? 'gold' : 'black'} />{' '}
        View
      </Button>
    </li>
  );
};

Snippet.propTypes = {
  document: PropTypes.object.isRequired,
  snip: PropTypes.shape({ id: PropTypes.number, text: PropTypes.string })
    .isRequired,
  highlightSnippet: PropTypes.func.isRequired
};

const SnippetList = ({
  pageWidth,
  pageHeight,
  snippets,
  highlightSnippet,
  showPdf
}) => {
  if (!snippets || snippets.length === 0) {
    return null;
  }

  let style;
  if (showPdf) {
    style = {
      position: 'absolute',
      left: `${pageWidth}px`,
      height: `${pageHeight + 30}px`,
      overflowY: 'scroll'
    };
  } else {
    style = {
      height: `${pageHeight + 30}px`,
      overflowY: 'scroll'
    };
  }

  return (
    <div style={style}>
      <h4>Snippets matching search</h4>
      <ol>
        {snippets.map((snip) => (
          <Snippet
            document={document}
            highlightSnippet={highlightSnippet}
            snip={snip}
            key={snip.id}
          />
        ))}
      </ol>
    </div>
  );
};

SnippetList.propTypes = {
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  snippets: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, text: PropTypes.string })
  ),
  highlightSnippet: PropTypes.func,
  showPdf: PropTypes.bool
};

const ViewButtons = ({ view, setView }) => {
  return (
    <ButtonGroup>
      <Button
        onClick={() => setView('snippets')}
        variant={view === 'snippets' ? 'secondary' : 'outline-secondary'}
      >
        Snippets
      </Button>

      {getAvailableWidth() > 800 && (
        <Button
          onClick={() => setView('both')}
          variant={view === 'both' ? 'secondary' : 'outline-secondary'}
        >
          Both
        </Button>
      )}
      <Button
        onClick={() => setView('document')}
        name="document"
        variant={view === 'document' ? 'secondary' : 'outline-secondary'}
      >
        Document
      </Button>
    </ButtonGroup>
  );
};
ViewButtons.propTypes = {
  view: PropTypes.string,
  setView: PropTypes.func
};

const PdfPreviewModal = ({ overlays, setOverlays }) => {
  let [
    { selectedDocument, searchString, pageNumber },
    dispatch
  ] = useAppState();
  const [snippets, setSnippets] = useState(null);

  const [view, setView] = useState(
    getAvailableWidth() > 800 ? 'both' : 'document'
  );

  if (!snippets) {
    const query = {
      method: 'solr',
      textQuery: searchString,
      documentId: selectedDocument.id
    };

    fetch('/nlp/snippets/searchSingleDocument', {
      method: 'POST', // or 'PUT'
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(query)
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Success:', data);
        setSnippets(data.snippets);
      })
      .catch((error) => {
        console.error('Error on search:', error);
      });
  }

  const showSnippets =
    snippets && snippets.length > 0 && (view === 'snippets' || view === 'both');

  const showPdf = view === 'document' || view === 'both';

  let pageWidth;
  if (showSnippets) {
    pageWidth = Math.min(
      getAvailableWidth() / 2 - 20,
      (getAvailableHeight() - 150) / Math.sqrt(2)
    );
  } else {
    pageWidth = Math.min(
      getAvailableWidth() - 20,
      (getAvailableHeight() - 150) / Math.sqrt(2)
    );
  }

  const handleClose = () =>
    dispatch({
      type: 'SET_PDF_MODAL_OPEN',
      pdfModalOpen: false
    });

  const highlightSnippet = (snippet) => {
    if (view === 'snippets') {
      setView('document');
    }

    dispatch({
      type: 'SET_SELECTED_SNIPPET',
      selectedSnippet: snippet ? snippet.id : null
    });
    setOverlays(snippet ? [snippet.misc] : []);
    dispatch({
      type: 'SET_PAGE_NUMBER',
      pageNumber: snippet ? +snippet.misc.page : 1
    });
  };

  const pageHeight = Math.sqrt(2) * pageWidth;

  // const modalWidth = showSnippets && showPdf ? 2 * pageWidth : pageWidth;
  let modalWidth;
  if (showSnippets && showPdf) {
    modalWidth = 2 * pageWidth;
  } else if (showPdf) {
    modalWidth = pageWidth;
  } else if (showSnippets) {
    modalWidth = Math.min(
      Math.max(0.8 * getAvailableWidth(), pageWidth),
      2 * pageWidth
    );
  }

  return (
    <>
      <Modal show={true} onHide={handleClose} id={'pdfModal'}>
        <Modal.Header
          closeButton
          style={{
            width: `${modalWidth}px`,
            display: 'block'
          }}
        >
          {selectedDocument && <h4>{getDisplayName(selectedDocument)}</h4>}

          {snippets && snippets.length > 0 && (
            <>
              <br />
              <ViewButtons view={view} setView={setView} />
            </>
          )}
        </Modal.Header>
        <Modal.Body
          style={{
            width: `${modalWidth}px`,
            height: `${pageHeight + 50}px`
          }}
        >
          {showPdf && (
            <div style={{ position: 'absolute', left: '0px' }}>
              {selectedDocument && (
                <Pdf
                  url={`/api/uploads/${selectedDocument.id}/${selectedDocument.filename}`}
                  pageNumber={pageNumber}
                  setPageNumber={(n) =>
                    dispatch({ type: 'SET_PAGE_NUMBER', pageNumber: n })
                  }
                  searchString={searchString}
                  overlays={overlays}
                  className="pdf-div"
                  pageWidth={pageWidth}
                />
              )}
            </div>
          )}

          {showSnippets && (
            <SnippetList
              showPdf={showPdf}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              snippets={snippets}
              highlightSnippet={highlightSnippet}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
PdfPreviewModal.propTypes = {
  setPdfModalUpdate: PropTypes.func,
  setOverlays: PropTypes.func,
  overlays: PropTypes.arrayOf(
    PropTypes.shape({
      page: PropTypes.string,
      top: PropTypes.string,
      bottom: PropTypes.string,
      'page-height': PropTypes.string
    })
  )
};

export default PdfPreviewModal;
